import { formatMoney, surchargeFor } from '@app/shared'
import { orgHref } from '../lib/public-router'
import type { RouterOutputs } from '../lib/trpc'
import {
  giftThanks,
  membershipThanks,
  standingGiftThanks,
  type Thanks,
} from '../lib/two-step-payment'
import { type DonateAnswers, GIFT } from './donate-answers'
import { givingChoiceOffered, kioskHref } from './kiosk'
import type { MembershipAnswers } from './membership-answers'

export const money = formatMoney

export type DonateMenuItem = RouterOutputs['donation']['menu'][number]

/** Joining the org, or paying for a membership again. */
export const MEMBERSHIP = 'membership'
/** The first question's way to a member's own account, rather than a gift. */
export const ACCOUNT = 'account'

/** Asked in one place, so the region and the set of chips cannot word it apart. */
export const HOW_MUCH = 'How much would you like to give?'

export const HOW_OFTEN = 'How often would you like to give?'

export type Often = 'once' | 'monthly' | 'annual'

/** Once is first: it is what most givers mean, and the other two follow it. */
export const OFTEN: { value: Often; label: string }[] = [
  { value: 'once', label: 'Once' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Yearly' },
]

/** The menu's own question, which an item's prompt may repeat word for word. */
export const MENU_ASK = 'What is this gift for?'

/** The page's own heading, beside the flow, naming who the gift is to. */
export function donateTitle(to: string): string {
  return `Give to ${to}`
}

/** What covering the card cost adds to the gift. */
export function coverFeeSays(giftCents: number | null, to: string): string {
  return giftCents === null
    ? `Adds the card processing cost, so the whole gift reaches ${to}.`
    : `Adds ${money(surchargeFor(giftCents))}, so the whole ${money(giftCents)} reaches ${to}.`
}

/**
 * The first question, derived rather than configured: a gift, and each drive
 * collecting today in the order the server put them — the one closing soonest
 * first. Membership stands where the org publishes a tier that is not
 * retired, and is absent where it does not: a branch offered by an org with no
 * ladder behind it is a question nobody can answer. A drive's row is a link to
 * the drive's own page, so it is never an answer this page holds.
 */
export function firstQuestion(
  orgShortId: string,
  to: string,
  offersMembership: boolean,
  collecting: RouterOutputs['donation']['campaigns'],
  offersAccount = false,
  kiosk = false,
) {
  const branches = [
    { key: GIFT, label: 'Donation', note: `A gift to ${to}` },
    ...(offersMembership && givingChoiceOffered('membership', kiosk)
      ? [
          {
            key: MEMBERSHIP,
            label: 'Membership',
            note: `Join ${to}, or pay for your membership`,
          },
        ]
      : []),
    /* Last, because it is not a gift: a member who came to pay what they owe,
       or to see their membership, is answered by their account rather than by
       another form here. */
    ...(offersAccount && givingChoiceOffered('account', kiosk)
      ? [
          {
            key: ACCOUNT,
            label: 'View my account',
            note: 'Already a member — your statement and membership. We will email you a private link.',
          },
        ]
      : []),
  ]
  const driveRows = collecting.map((c) => ({
    key: c.shortId,
    label: c.label,
    /* The drive's own line, where it has written one. An org that wrote none
       gets no second line rather than one we made up for it. */
    note: c.tagline ?? undefined,
    href: kioskHref(orgHref(orgShortId, 'donate', c.shortId), kiosk),
  }))
  return { branches, driveRows }
}

/** One item anywhere in the published menu, which nests to any depth. */
export function findDonateItem(
  items: DonateMenuItem[],
  id: string,
): DonateMenuItem | null {
  for (const item of items) {
    if (item.id === id) return item
    const under = findDonateItem(item.children, id)
    if (under) return under
  }
  return null
}

/**
 * The thank-you for what was just paid or saved, from the answers that named
 * it — a standing gift, a membership, a gift — or, where none survived the
 * return, one that names nothing rather than the wrong thing.
 */
export function thanksFor(paid: {
  standing: Parameters<typeof standingGiftThanks>[0] | null
  membership: MembershipAnswers | null
  gift: DonateAnswers | null
  on: string
}): Thanks {
  const { standing, membership, gift, on } = paid
  if (standing) return standingGiftThanks(standing)
  if (membership)
    return membershipThanks({
      tierLabel: membership.tierLabel,
      startsOn: membership.startsOn,
      dueTodayCents: membership.dueTodayCents,
      email: membership.donorEmail,
      on,
    })
  if (gift)
    return giftThanks({
      amountCents: gift.amountCents,
      coverFee: gift.coverFee,
      forLabel: gift.forLabel,
      note: gift.noteLabel
        ? { label: gift.noteLabel, words: gift.description }
        : null,
      email: gift.donorEmail,
      on,
    })
  return {
    headline: 'Thank you.',
    receipt: 'There is nothing else for you to do.',
    record: [],
  }
}
